import {
  SUBMISSION_QUERY_KEY,
  type TransformedSubmission,
  UNDERWRITING_QUESTIONS_QUERY_KEY,
  type UnderwritingQuestionsResponse,
} from 'api/submission';
import { skipConditionsData } from 'common/constants';
import { type Field, handleShowCondition } from 'components/lib/QuestionEngine';
import { formatThreeConfigVersion } from 'helpers/utils';
import { isUndefined } from 'lodash-es';
import { queryClient } from 'providers/reactQueryClient';
import type { QuoteFlowStep } from 'types/QuoteFlow';
import type { StepsSkipConditions, UpdateStepperPayload } from './quoteStepper.types';

/**
 * Check if a specific step is eligible to be shown
 */
export const isStepEligible = (payload: {
  submissionId: string;
  step: QuoteFlowStep;
  stepsSkipConditions?: StepsSkipConditions;
}) => {
  const { step, submissionId, stepsSkipConditions } = payload;
  const submission = queryClient.getQueryData([SUBMISSION_QUERY_KEY, submissionId]) as
    | TransformedSubmission
    | undefined;

  const questions = queryClient.getQueryData([UNDERWRITING_QUESTIONS_QUERY_KEY, submissionId]) as
    | UnderwritingQuestionsResponse
    | undefined;

  // Check if step should be skipped based on conditions
  const hasIgnoreCondition = skipConditionsData.find((condition) =>
    condition.stepKeyList.includes(step.key),
  );

  if (
    hasIgnoreCondition &&
    hasIgnoreCondition.ignoreIf(
      submission!,
      stepsSkipConditions ? stepsSkipConditions[step.key] : undefined,
    )
  ) {
    return false;
  }

  // Hardcoded steps are always eligible
  if (step.type === 'hardcoded') {
    return true;
  }

  // For dynamic steps, check if there are any visible questions
  const policyData = {
    pol_state: submission?.us_state,
    ...submission?.data?.policy_data,
    ...submission?.data?.uw_questions,
    productVersion: formatThreeConfigVersion(submission?.three_config_version),
  };

  const fields =
    questions?.flattenQuestions.find((question) => question.code === step.key)?.nested_fields || [];
  const hasVisibleQuestion = fields.some(
    (field) => handleShowCondition(field, policyData) && !field?.is_hidden,
  );

  return hasVisibleQuestion;
};

/**
 * Find a dynamic step that will render a question
 *
 * Until a hardcoded step or a dynamic step which renders question,
 * do the checks.
 * It checks the steps in linear order.
 * Example: a->b-c->d
 * If b has ignore condition it moves to c and checks the c.
 */
export const findEligibleStep = (payload: {
  submissionId: string;
  currentSectionSteps: QuoteFlowStep[];
  currentStep: number;
  destination: UpdateStepperPayload['destination'];
  stepsSkipConditions?: StepsSkipConditions;
}) => {
  const { currentStep, currentSectionSteps, destination, submissionId, stepsSkipConditions } =
    payload;
  const submission = queryClient.getQueryData([SUBMISSION_QUERY_KEY, submissionId]) as
    | TransformedSubmission
    | undefined;

  const questions = queryClient.getQueryData([UNDERWRITING_QUESTIONS_QUERY_KEY, submissionId]) as
    | UnderwritingQuestionsResponse
    | undefined;

  const policyData = {
    pol_state: submission?.us_state,
    ...submission?.data?.policy_data,
    ...submission?.data?.uw_questions,
    productVersion: formatThreeConfigVersion(submission?.three_config_version),
  };

  const foundFields = (step: QuoteFlowStep) =>
    questions?.flattenQuestions.find((question) => question.code === step.key)?.nested_fields || [];

  const foundVisibleQuestion = (fields: Field[]) =>
    fields.find((field) => handleShowCondition(field, policyData) && !field?.is_hidden);

  let targetStepIndex = destination === 'next' ? currentStep + 1 : currentStep - 1;
  let foundEligibleStep = false;

  while (!foundEligibleStep) {
    const selectedStep = currentSectionSteps[targetStepIndex];
    const hasIgnoreCondition = skipConditionsData.find((condition) =>
      condition.stepKeyList.includes(selectedStep?.key),
    );

    if (
      hasIgnoreCondition &&
      hasIgnoreCondition.ignoreIf(
        submission!,
        stepsSkipConditions ? stepsSkipConditions[selectedStep?.key] : undefined,
      )
    ) {
      targetStepIndex += destination === 'next' ? 1 : -1;
    } else {
      const targetStepData = currentSectionSteps[targetStepIndex];

      if (!targetStepData) {
        // No steps to check, break to avoid infinite loop or undefined access
        break;
      }

      if (targetStepData.type === 'hardcoded') {
        foundEligibleStep = true;
        break;
      }

      const fields = foundFields(targetStepData);
      const hasQuestion = !isUndefined(foundVisibleQuestion(fields));

      if (hasQuestion) {
        foundEligibleStep = true;
        break;
      }

      targetStepIndex += destination === 'next' ? 1 : -1;
    }
  }

  const stepChangeCount = Math.abs(currentStep - targetStepIndex);

  return { stepChangeCount };
};
